import { Camera, ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { PlantacionDeInforme } from "@/lib/informes";
import { formatearCifra, formatearMonto } from "@/lib/montos";
import { useResaltadoDeSincronizaciones } from "@/hooks/useResaltadoDeSincronizaciones";
import { cn } from "@/lib/utils";

import { conteoDeRegistrosDelItem } from "./aguasTerreno";
import type { GrupoDeLaVista } from "./cuadroDePlantaciones";
import { FilaDeTerrenoDeLaPlantacion } from "./FilaDeTerrenoDeLaPlantacion";
import { marcaDeFila } from "./MarcasDeSincronizacion";
import { nombreDeLaPlantacion, type TotalDePlantaciones } from "./plantaciones";
import {
  claveDePlantacion,
  claveDePlantacionDeTerreno,
  creadaEnTerrenoSinValorizar,
  gruposDeTerrenoSueltos,
  itemDeLaPlantacion,
  nombreDelItem,
  opcionesParaVincular,
  porEnlazarDeLaEspecie,
  type ItemDePlantacionPorEnlazar,
  type TerrenoDeLaLista,
} from "./plantacionesTerreno";
import { formatearSuperficie } from "./superficies";

interface TablaDePlantacionesProps {
  grupos: GrupoDeLaVista[];
  /** Las claves de los grupos plegados. */
  plegados: ReadonlySet<string>;
  /** El pie del cuadro entero: describe el informe, no la búsqueda. */
  total: TotalDePlantaciones;
  /** Alguna fila tiene superficie: sin ninguna, el pie dice "—" y no `0`. */
  totalConSuperficie: boolean;
  ocupada: boolean;
  onAlternarGrupo: (clave: string) => void;
  onAbrir: (fila: PlantacionDeInforme) => void;
  onValidar: (fila: PlantacionDeInforme, validada: boolean) => void;
  /** TODAS las guardadas, no solo las visibles: "Es la misma que" no depende del filtro. */
  todas?: PlantacionDeInforme[];
  /** Lo que el encargado dejó en el predio; sin él la tabla es la de siempre. */
  terreno?: TerrenoDeLaLista;
  /** Qué registros de terreno están desplegados, por clave de fila. */
  abiertas?: ReadonlySet<string>;
  onAlternar?: (clave: string) => void;
  /** Completar una plantación de terreno: abre el formulario de alta precargado. */
  onAdoptar?: (porEnlazar: ItemDePlantacionPorEnlazar) => void;
}

/** Las columnas de la tabla: la línea de terreno y los encabezados de grupo ocupan todas. */
const COLUMNAS = 12;

function Cifra({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("font-mono text-xs font-semibold", className)}>{children}</span>;
}

/** Lo que se imprime cuando la fila no tiene el dato: un guion apagado. */
function Vacio() {
  return <span className="text-ink-ghost">—</span>;
}

/**
 * La tabla de la lista: AGRUPADA POR ESPECIE, una fila por cuartel.
 *
 * LA ESPECIE NO ES COLUMNA: encabeza cada grupo, así que la primera columna es
 * la variedad. Hacer clic en el grupo lo pliega; hacer clic en una fila abre el
 * modal. Los dos tienen además un botón real: un `onClick` en un `<tr>` no lo
 * alcanza el teclado. La casilla frena la propagación —marcar no puede abrir
 * el detalle—.
 *
 * LOS PIES DE GRUPO Y DEL CUADRO SUMAN LO QUE EL SERVIDOR CALCULÓ (plantas ya
 * redondeadas por fila) y las superficies en centésimas; las filas sin dato no
 * suman y tampoco anulan el pie. Con ninguna fila cargada imprimen "—" y
 * nunca `0`.
 */
export function TablaDePlantaciones({
  grupos,
  plegados,
  total,
  totalConSuperficie,
  ocupada,
  onAlternarGrupo,
  onAbrir,
  onValidar,
  todas,
  terreno,
  abiertas,
  onAlternar,
  onAdoptar,
}: TablaDePlantacionesProps) {
  const derecha = "text-right";
  const opciones = opcionesParaVincular(todas ?? grupos.flatMap((g) => g.filas));
  const resaltado = useResaltadoDeSincronizaciones();
  const sueltos = terreno ? gruposDeTerrenoSueltos(terreno.porEnlazar, grupos.map((g) => g.clave)) : [];

  /** La fila de una plantación de terreno sin guardar, con su línea de terreno. */
  function filaPorEnlazar(porEnlazar: ItemDePlantacionPorEnlazar) {
    if (!terreno) return null;

    const { uuid, item } = porEnlazar;
    const clave = claveDePlantacionDeTerreno(uuid);
    const marca = marcaDeFila(resaltado.deItem(item));
    const nombre = nombreDelItem(item);
    const fotos = conteoDeRegistrosDelItem(item).fotos;

    return (
      <Fragment key={uuid}>
        <TableRow id={clave} onClick={() => onAdoptar?.(porEnlazar)} {...marca.atributo}
        className={cn("h-10 cursor-pointer", marca.claseFila)}>
          <TableCell className={cn("max-w-[17.5rem] pl-10", marca.claseCelda)}>
            <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              aria-label={`Completar ${nombre}`}
              onClick={(e) => {
                e.stopPropagation();
                onAdoptar?.(porEnlazar);
              }}
              className="text-ink focus-visible:ring-ring/50 block max-w-full truncate rounded-sm text-[0.78125rem] font-bold outline-none focus-visible:ring-[3px]"
            >
              {item.variedad?.trim() ? item.variedad : <Vacio />}
            </button>
            {marca.tag}
          </div>
          </TableCell>
          <TableCell className={derecha}>
            <Cifra>{item.anio_plantacion ?? <Vacio />}</Cifra>
          </TableCell>
          <TableCell className={derecha}>
            <Cifra className="font-bold">
              {item.superficie_ha == null ? <Vacio /> : formatearSuperficie(item.superficie_ha)}
            </Cifra>
          </TableCell>
          <TableCell>
            <Cifra>{item.cuartel ?? <Vacio />}</Cifra>
          </TableCell>
          <TableCell>
            <span className="text-xs font-semibold">{item.portainjerto ?? <Vacio />}</span>
          </TableCell>
          <TableCell className={derecha}>
            <Cifra>{item.distancia_plantacion ?? <Vacio />}</Cifra>
          </TableCell>
          <TableCell className={derecha}>
            <Cifra>
              {item.plantas_por_ha == null ? <Vacio /> : formatearMonto(item.plantas_por_ha, null)}
            </Cifra>
          </TableCell>
          <TableCell className={derecha}>
            {/* Las plantas totales las calcula el servidor al guardar. */}
            <Cifra className="font-bold">
              <Vacio />
            </Cifra>
          </TableCell>
          <TableCell>
            <span className="text-xs font-semibold">{item.sistema_riego ?? <Vacio />}</span>
          </TableCell>
          <TableCell>
            <span
              aria-label={fotos === 1 ? "1 foto" : `${fotos} fotos`}
              className="text-ink-muted inline-flex items-center gap-1"
            >
              <Camera className="size-3" aria-hidden="true" />
              <span className="font-mono text-[0.6875rem] font-bold">{fotos}</span>
            </span>
          </TableCell>
          <TableCell />
          <TableCell className="pr-3.5 text-right">
            <ChevronRight className="text-ink-ghost inline size-3.5" aria-hidden="true" />
          </TableCell>
        </TableRow>
        <FilaDeTerrenoDeLaPlantacion
          terreno={terreno}
          titulo={nombre}
          item={item}
          deTerreno
          uuidPorEnlazar={uuid}
          creadaSinValorizar
          opciones={opciones}
          abierto={abiertas?.has(clave) ?? false}
          ocupada={ocupada}
          columnas={COLUMNAS}
          onAlternar={() => onAlternar?.(clave)}
        />
      </Fragment>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead variant="versalitas" className="pl-5">
            Variedad
          </TableHead>
          <TableHead variant="versalitas" className={derecha}>Año</TableHead>
          <TableHead variant="versalitas" className={derecha}>Sup. ha</TableHead>
          <TableHead variant="versalitas">Cuartel</TableHead>
          <TableHead variant="versalitas">Portainjerto</TableHead>
          <TableHead variant="versalitas" className={derecha}>Distancia</TableHead>
          <TableHead variant="versalitas" className={derecha}>Plantas/ha</TableHead>
          <TableHead variant="versalitas" className={derecha}>Plantas tot.</TableHead>
          <TableHead variant="versalitas">Riego</TableHead>
          <TableHead variant="versalitas">Fotos</TableHead>
          <TableHead variant="versalitas" className="text-center">Valid.</TableHead>
          <TableHead className="w-7 pr-3.5">
            <span className="sr-only">Abrir</span>
          </TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {grupos.map((grupo) => {
          const plegado = plegados.has(grupo.clave);
          const todasValidadas = grupo.validadas === grupo.cantidad;

          return (
            // UN `Fragment` CON `key` Y NO UN `<>`: cada grupo aporta varias
            // filas hermanas y sin la clave React remontaría el bloque entero.
            <Fragment key={grupo.clave}>
              <TableRow
                onClick={() => onAlternarGrupo(grupo.clave)}
                className="bg-muted/60 hover:bg-muted/60 h-10 cursor-pointer"
              >
                <TableCell colSpan={2} className="pl-5">
                  <div className="flex items-center gap-2">
                    {plegado ? (
                      <ChevronRight className="text-ink-muted size-3.5 flex-none" aria-hidden="true" />
                    ) : (
                      <ChevronDown className="text-ink-muted size-3.5 flex-none" aria-hidden="true" />
                    )}
                    <button
                      type="button"
                      aria-expanded={!plegado}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAlternarGrupo(grupo.clave);
                      }}
                      className="text-ink focus-visible:ring-ring/50 rounded-sm text-[0.8125rem] font-extrabold outline-none focus-visible:ring-[3px]"
                    >
                      {grupo.especie}
                    </button>
                    <span className="text-ink-muted font-mono text-[0.6875rem] font-bold">
                      {grupo.cantidad}
                    </span>
                  </div>
                </TableCell>
                <TableCell className={derecha}>
                  <span className="font-mono text-[0.78125rem] font-extrabold">
                    {grupo.conSuperficie ? formatearSuperficie(grupo.total.superficieHa) : "—"}
                  </span>
                </TableCell>
                <TableCell colSpan={4}>
                  {grupo.alerta ? (
                    <span className="text-warning-soft-foreground text-[0.6875rem] font-bold">
                      {grupo.alerta}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className={derecha}>
                  <span className="font-mono text-[0.78125rem] font-extrabold">
                    {formatearMonto(grupo.total.plantas, null)}
                  </span>
                </TableCell>
                <TableCell colSpan={2} />
                <TableCell className="text-center">
                  <span
                    aria-label={`${grupo.validadas} de ${grupo.cantidad} validadas`}
                    className={cn(
                      "font-mono text-[0.6875rem] font-bold",
                      todasValidadas ? "text-success-soft-foreground" : "text-ink-muted",
                    )}
                  >
                    {grupo.validadas}/{grupo.cantidad}
                  </span>
                </TableCell>
                <TableCell />
              </TableRow>

              {plegado
                ? null
                : grupo.filas.map((fila) => {
                    const nombre = nombreDeLaPlantacion(fila);
                    const claveDeFila = claveDePlantacion(fila.id);
                    const marca = marcaDeFila(resaltado.deItem(terreno ? itemDeLaPlantacion(terreno.items, fila) : null));

                    return (
                      <Fragment key={fila.id}>
                        <TableRow
                          id={`plantacion-${fila.id}`}
                          onClick={() => onAbrir(fila)}
                          {...marca.atributo}
                          className={cn("h-10 cursor-pointer", marca.claseFila)}
                        >
                          <TableCell className={cn("max-w-[17.5rem] pl-10", marca.claseCelda)}>
                            <div className="flex min-w-0 items-center gap-2">
                            <button
                              type="button"
                              aria-label={`Editar ${nombre}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                onAbrir(fila);
                              }}
                              className="text-ink focus-visible:ring-ring/50 block max-w-full truncate rounded-sm text-[0.78125rem] font-bold outline-none focus-visible:ring-[3px]"
                            >
                              {fila.variedad?.trim() ? fila.variedad : <Vacio />}
                            </button>
                            {marca.tag}
                          </div>
                          </TableCell>
                          <TableCell className={derecha}>
                            <Cifra>{fila.anio_plantacion ?? <Vacio />}</Cifra>
                          </TableCell>
                          <TableCell className={derecha}>
                            <Cifra className="font-bold">
                              {fila.superficie_ha === null ? <Vacio /> : formatearCifra(fila.superficie_ha)}
                            </Cifra>
                          </TableCell>
                          <TableCell>
                            <Cifra>{fila.cuartel ?? <Vacio />}</Cifra>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs font-semibold">{fila.portainjerto ?? <Vacio />}</span>
                          </TableCell>
                          <TableCell className={derecha}>
                            <Cifra>{fila.distancia_plantacion ?? <Vacio />}</Cifra>
                          </TableCell>
                          <TableCell className={derecha}>
                            <Cifra>
                              {fila.plantas_por_ha === null ? (
                                <Vacio />
                              ) : (
                                formatearMonto(fila.plantas_por_ha, null)
                              )}
                            </Cifra>
                          </TableCell>
                          <TableCell className={derecha}>
                            <Cifra className="font-bold">
                              {fila.plantas_totales === null ? (
                                <Vacio />
                              ) : (
                                formatearMonto(fila.plantas_totales, null)
                              )}
                            </Cifra>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs font-semibold">{fila.sistema_riego ?? <Vacio />}</span>
                          </TableCell>
                          <TableCell>
                            <span
                              aria-label={`${fila.fotos?.length ?? 0} fotos`}
                              className="text-ink-muted inline-flex items-center gap-1"
                            >
                              <Camera className="size-3" aria-hidden="true" />
                              <span className="font-mono text-[0.6875rem] font-bold">
                                {fila.fotos?.length ?? 0}
                              </span>
                            </span>
                          </TableCell>
                          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              aria-label={`Validar ${nombre}`}
                              checked={fila.validada}
                              disabled={ocupada}
                              title={fila.validada ? "Quitar validación" : "Marcar los datos como revisados"}
                              // SE GUARDA SOLA y viaja sin el resto de los campos,
                              // para no pisar lo que otro pudo haber corregido
                              // mientras tanto.
                              onCheckedChange={(v) => onValidar(fila, v === true)}
                            />
                          </TableCell>
                          <TableCell className="pr-3.5 text-right">
                            <ChevronRight className="text-ink-ghost inline size-3.5" aria-hidden="true" />
                          </TableCell>
                        </TableRow>
                      {terreno ? (
                        <FilaDeTerrenoDeLaPlantacion
                          terreno={terreno}
                          titulo={nombre}
                          item={itemDeLaPlantacion(terreno.items, fila)}
                          deTerreno={fila.terreno_ref !== null}
                          opciones={opciones}
                          abierto={abiertas?.has(claveDeFila) ?? false}
                          ocupada={ocupada}
                          columnas={COLUMNAS}
                          onAlternar={() => onAlternar?.(claveDeFila)}
                          creadaSinValorizar={creadaEnTerrenoSinValorizar(fila)}
                        />
                      ) : null}
                      </Fragment>
                    );
                  })}

              {/* LAS DE TERRENO CAEN EN EL GRUPO DE SU ESPECIE (misma clave
                  normalizada que agrupa), después de las guardadas. */}
              {plegado || !terreno
                ? null
                : porEnlazarDeLaEspecie(terreno.porEnlazar, grupo.clave).map(filaPorEnlazar)}
            </Fragment>
          );
        })}

        {sueltos.map((grupo) => {
          const plegado = plegados.has(grupo.clave);

          return (
            <Fragment key={grupo.clave}>
              <TableRow
                onClick={() => onAlternarGrupo(grupo.clave)}
                className="bg-muted/60 hover:bg-muted/60 h-10 cursor-pointer"
              >
                <TableCell colSpan={COLUMNAS} className="pl-5">
                  <div className="flex items-center gap-2">
                    {plegado ? (
                      <ChevronRight className="text-ink-muted size-3.5 flex-none" aria-hidden="true" />
                    ) : (
                      <ChevronDown className="text-ink-muted size-3.5 flex-none" aria-hidden="true" />
                    )}
                    <button
                      type="button"
                      aria-expanded={!plegado}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAlternarGrupo(grupo.clave);
                      }}
                      className="text-ink focus-visible:ring-ring/50 rounded-sm text-[0.8125rem] font-extrabold outline-none focus-visible:ring-[3px]"
                    >
                      {grupo.especie}
                    </button>
                    <span className="text-ink-muted font-mono text-[0.6875rem] font-bold">
                      {grupo.porEnlazar.length}
                    </span>
                  </div>
                </TableCell>
              </TableRow>
              {plegado ? null : grupo.porEnlazar.map(filaPorEnlazar)}
            </Fragment>
          );
        })}

        <TableRow className="bg-superficie-sutil hover:bg-superficie-sutil h-10">
          <TableCell colSpan={2} className="text-ink pl-5 text-xs font-extrabold">
            Total plantaciones
          </TableCell>
          <TableCell className={derecha}>
            <span className="font-mono text-[0.8125rem] font-extrabold">
              {totalConSuperficie ? formatearSuperficie(total.superficieHa) : "—"}
            </span>
          </TableCell>
          <TableCell colSpan={4} />
          <TableCell className={derecha}>
            <span className="font-mono text-[0.8125rem] font-extrabold">
              {formatearMonto(total.plantas, null)}
            </span>
          </TableCell>
          <TableCell colSpan={4} className="text-ink-muted pr-3.5 text-[0.6875rem] font-semibold whitespace-normal">
            Plantas = superficie × plantas por ha, redondeadas fila a fila. Las filas sin esos
            datos no suman.
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}
